import { useQuery, useQueries } from '@tanstack/react-query'
import { useApiClient } from '../useApiClient'
import { walkTimeline, walkRatio, type WalkTimelineRow } from '../endpoints/walks'
import {
  pivot as pivotTimeline,
  seriesKeysWindowFirst,
  hasMultipleValueTypes,
} from '~/components/Charts/TimelineChart'
import {
  assignGroups,
  summariseByUnit,
  partitionWithRemainder,
  computePanelAggregates,
  computeGrowth,
  computeSeriesSums,
  splitBaseline,
  baselineBoundaryIso,
  pointBucketUnit,
  type SeriesPoint,
  type Group,
  type GranularityUnit,
} from '~/components/Charts/dataPipeline'
import {
  aggregateRatioRowsWithComponents,
  ratioPanelSum,
  ratioSeriesPanelSums,
  type RatioComponentsByBucket,
} from '~/components/Charts/aggregateRatioRows'
import { useStore } from '~/state/useStore'
import { clampFromToFloor, useModelFloorIso } from '~/state/modelFloor'
import { toLongDate } from '~/util/date'
import { MESSAGES } from '~/components/Charts/chrome/chartChrome'
import { distinctValueTypes } from '~/components/Charts/seriesAdditivity'

export type DynamicsWalkFamily = 'timeline' | 'ratio'

export interface DynamicsTimeframe {
  id: string
  label: string
  from: string
  to: string
  /** Campaigns C1 Task 7: per-arm override — a campaign arm's merged+fact-pruned predicate.
   *  Replaces `filterDims` for THIS arm's walk only; absent = `filterDims` (unchanged today). */
  filter_dims?: Record<string, string[]>
}

export interface DynamicsPanel {
  key: string
  label: string
  points: SeriesPoint[]
  aggregate: number
  avg: number
  growth: number | null
  seriesSums: Map<string, number | null>
  /** Row 15-light: true for the prepended previous-period baseline panel (grayed render, no own Δ). */
  isBaseline?: boolean
}

export interface DynamicsPanels {
  panels: DynamicsPanel[]
  groups: Group[]
  isLoading: boolean
  isFetching: boolean
  isPlaceholderData: boolean
  isError: boolean
  error: Error | null
  hiddenCount: number
  /** Sub-period single-mode ratio components for downstream panel Σ consumers (e.g. summary
   *  matrix). null in per-timeframe mode and for the volume family. */
  ratioComponents: RatioComponentsByBucket | null
  /** Task 12: distinct value_types across the arrived rows (both dual-mode sources — see the
   *  single `sourceRows` derivation below), for the caller's own additivity resolver call
   *  (chart-semantics doctrine — replaces Task 3's `[]` placeholder). Real rows only; never
   *  undefined (empty when the walk is disabled/pending/empty). */
  valueTypes: string[]
}

export interface UseDynamicsPanelsArgs {
  family: DynamicsWalkFamily
  kpi: string
  factTable: string
  filterDims: Record<string, string[]>
  groupBy: string[]
  bucketSize: 'day' | 'week' | 'month' | 'quarter' | 'year'
  fromIso: string
  toIso: string
  unit: GranularityUnit
  dots: number
  timeframes: DynamicsTimeframe[]
  /** Row 15-light: when non-null, the single-window walk extends its `from` to this and
   *  buildSubPeriodPanels prepends the previous-period baseline panel. Per-timeframe mode ignores it
   *  (panels there are per-TF overlays, not sub-periods of one window). */
  baselineFromIso?: string | null
  enabled?: boolean
  /** base -> catalog-slot hex (spec D3); forwarded to the internal assignGroups call. Callers
   *  derive this via useDimValueColorForBase — ONE catalog shared across metric/driver/compare
   *  (P6: same dim value = same color across arms), mirroring resolveArmColoring's pattern. */
  colorForBase?: (base: string) => string | undefined
}

export interface BuiltPanels {
  panels: DynamicsPanel[]
  hiddenCount: number
}

function periodRangeLabel(points: SeriesPoint[]): string {
  const first = points[0]?.time
  if (!first) return ''
  try {
    return toLongDate(String(first))
  } catch {
    return String(first)
  }
}

/** Sub-period mode panel math (family-aware). Volume: summariseByUnit pipeline (unchanged).
 *  Ratio: coarsen via aggregateRatioRowsWithComponents, partition the coarsened points, and
 *  compute each panel's Σ as Σnum/Σdenom (denom once per bucket) — never a sum of ratios.
 *  ø stays the mean of non-null bucket values for both families (spec §2).
 *
 *  Row 15-light: when `windowFromIso` is set, `rows` are the EXTENDED walk result;
 *  points before the window-from's bucket boundary become a prepended previous-period baseline panel whose
 *  Σ/ø/seriesSums follow the SAME family-aware rules (the ratio components cover the baseline
 *  buckets because they were built from the extended rows). partitionWithRemainder runs on the
 *  IN-WINDOW points only, so the remainder/hidden math cannot shift vs. the flag-off rendering. */
export function buildSubPeriodPanels(
  rows: WalkTimelineRow[],
  family: DynamicsWalkFamily,
  unit: GranularityUnit,
  dots: number,
  groups: Group[],
  windowFromIso?: string | null,
  // The grain the ROWS arrived at — the walk's governed bucket_size. NOT the split boundary
  // itself: `points` below are these rows folded at `unit`, so the boundary is the coarser of the
  // two (pointBucketUnit), and the window-from's snapped edge bucket must stay IN-WINDOW
  // (coarse-rollup window-edge gotcha). Defaults to `unit` (rows already at the display grain).
  rowBucketUnit: GranularityUnit = unit,
): BuiltPanels & { components: RatioComponentsByBucket | null } {
  let points: SeriesPoint[]
  let components: RatioComponentsByBucket | null = null
  if (family === 'ratio') {
    // runningTotal: false — Dynamics has no running-total knob (unlike ChartCanvas tiles).
    // If one is added, thread it here; components stay non-cumulative regardless.
    const agg = aggregateRatioRowsWithComponents(rows, unit, false)
    points = pivotTimeline(agg.rows).points
    components = agg.components
  } else {
    const piv = pivotTimeline(rows)
    points = summariseByUnit(piv.points, unit, groups)
  }
  let baselinePoints: SeriesPoint[] = []
  if (windowFromIso != null) {
    // Split at the grain `points` actually carry — the coarser of the fold unit and the row
    // grain. Passing `rowBucketUnit` straight through was correct only while `requested <=
    // effective === bucket_size` held; at fy (folded here, wired as 'month') it would split at
    // startOfMonth and drop the window's OPENING fiscal year into the baseline panel: Σ inflated
    // by a whole FY, in-window short its first bucket, Δ chain seeded wrong. Non-fy units pass
    // through unchanged.
    const split = splitBaseline(points, windowFromIso, pointBucketUnit(unit, rowBucketUnit))
    baselinePoints = split.baseline
    points = split.inWindow
  }
  const { panels: subPeriods, hidden } = partitionWithRemainder(points, dots)

  const panels: DynamicsPanel[] = []
  // Δ-chain seed: prevSum starts as the baseline Σ when a NON-EMPTY baseline exists, else null.
  // (Deliberate restructure of the old `i > 0 && prevSum !== null` guard: the first in-window
  // panel now computes growth whenever a seed exists; without a baseline prevSum starts null →
  // growth null, exactly as before. An empty baseline must NOT seed a phantom 0-based Δ.)
  let prevSum: number | null = null
  if (windowFromIso != null) {
    const times = baselinePoints.map((p) => String(p.time))
    const { sum: volumeSum, avg } = computePanelAggregates(baselinePoints, groups)
    const ratioSum = components ? ratioPanelSum(times, components) : null
    const hasData = baselinePoints.length > 0
    panels.push({
      key: 'baseline',
      label: MESSAGES.previousPeriod,
      isBaseline: true,
      points: baselinePoints,
      aggregate: components ? (ratioSum ?? 0) : volumeSum,
      avg,
      growth: null,
      // Empty pre-window → empty sums map (matrix renders 'Σ –', not a misleading 0).
      seriesSums: !hasData
        ? new Map<string, number | null>()
        : components
          ? ratioSeriesPanelSums(times, components)
          : computeSeriesSums(baselinePoints, groups),
    })
    // Ratio seed stays null on a zero-denominator baseline (ratioSum null) — same rationale.
    prevSum = hasData ? (components ? ratioSum : volumeSum) : null
  }
  subPeriods.forEach((panelPoints, i) => {
    const { sum: volumeSum, avg } = computePanelAggregates(panelPoints, groups)
    const sum = components
      ? (ratioPanelSum(
          panelPoints.map((p) => String(p.time)),
          components,
        ) ?? 0)
      : volumeSum
    const growth = prevSum !== null ? computeGrowth(prevSum, sum) : null
    prevSum = sum
    panels.push({
      key: `sub_${i}`,
      label: periodRangeLabel(panelPoints),
      points: panelPoints,
      aggregate: sum,
      avg,
      growth,
      seriesSums: components
        ? ratioSeriesPanelSums(
            panelPoints.map((p) => String(p.time)),
            components,
          )
        : computeSeriesSums(panelPoints, groups),
    })
  })
  return { panels, hiddenCount: hidden.length, components }
}

/** Per-timeframe mode: one continuous panel per timeframe; ratio Σ from that timeframe's
 *  own components (its rows are independent walks — components are per-panel).
 *  NOTE: ratio per-TF panels were previously pivoting RAW rows (daily); they are now coarsened
 *  by `unit`, matching the sub-period path. Volume per-TF keeps the raw pivot at every unit
 *  EXCEPT 'fy' — see the fold guard below for why fy is the one case that must differ. */
export function buildPerTimeframePanels(
  rowsPerTf: WalkTimelineRow[][],
  tfs: DynamicsTimeframe[],
  family: DynamicsWalkFamily,
  unit: GranularityUnit,
  groups: Group[],
  // FU-h: the MERGED-rows showVt decision — groups are derived from ALL timeframes' rows,
  // so each per-TF pivot must key in the same namespace (else ratio matrix cells read
  // 'Σ –' and volume panels silently render Σ 0 with empty charts).
  showVt?: boolean,
): BuiltPanels {
  let prevSum: number | null = null
  const panels = tfs.map((tf, i) => {
    const rows = rowsPerTf[i] ?? []
    let points: SeriesPoint[]
    let sum: number
    let avg: number
    let seriesSums: Map<string, number | null>
    if (family === 'ratio') {
      const agg = aggregateRatioRowsWithComponents(rows, unit, false, showVt)
      points = pivotTimeline(agg.rows, showVt).points
      const a = computePanelAggregates(points, groups)
      avg = a.avg
      const times = points.map((p) => String(p.time))
      sum = ratioPanelSum(times, agg.components) ?? 0
      seriesSums = ratioSeriesPanelSums(times, agg.components)
    } else {
      const piv = pivotTimeline(rows, showVt)
      // FINAL-REVIEW I2 — fold the volume arm at the requested unit when, and only when, that
      // unit is 'fy'.
      //
      // At the five ladder units the raw pivot ALREADY carries the requested grain (the walk is
      // bucketed at the governed unit, which is never finer than the request), so summariseByUnit
      // would be a no-op-shaped rewrite of the same buckets — keeping the raw array makes those
      // paths byte-identical by construction rather than by argument. FY is the one display unit
      // coarser than its own wire bucket, so without this a monthly driver row renders beside an
      // FY-folded ratio metric row: two grains on one page under a "Fiscal Years" picker, with no
      // explanation. FY is new, so there is no prior fy behaviour to preserve.
      //
      // Σ is invariant under the fold (summing months into their fiscal year preserves the total);
      // ø becomes the mean per FY bucket instead of per month — the same ø the sub-period path
      // already reports at fy.
      points = unit === 'fy' ? summariseByUnit(piv.points, unit, groups) : piv.points
      const a = computePanelAggregates(points, groups)
      sum = a.sum
      avg = a.avg
      seriesSums = computeSeriesSums(points, groups)
    }
    const growth = i > 0 && prevSum !== null ? computeGrowth(prevSum, sum) : null
    prevSum = sum
    return { key: tf.id, label: tf.label, points, aggregate: sum, avg, growth, seriesSums }
  })
  return { panels, hiddenCount: 0 }
}

/**
 * Yields aligned Panel[] for the merged-Dynamics gestalt, from one of two data sources keyed on
 * picker state: empty `timeframes` -> one timeline/ratio walk over [from,to] then partition into
 * sub-period panels (Campaign Dynamics behaviour); >=1 timeframes -> one walk per timeframe, one
 * continuous panel each (Multi Dynamics behaviour). Both internal data sources are called
 * UNCONDITIONALLY (hooks rules); the inactive one is disabled (empty queries / null params).
 */
export function useDynamicsPanels(args: UseDynamicsPanelsArgs): DynamicsPanels {
  const {
    family,
    kpi,
    factTable,
    filterDims,
    groupBy,
    bucketSize,
    fromIso,
    toIso,
    unit,
    dots,
    timeframes,
    baselineFromIso = null,
    enabled = true,
    colorForBase,
  } = args
  const client = useApiClient()
  const palette = useStore((s) => s.personal.chipPalette)
  const floorIso = useModelFloorIso()

  const perTimeframe = enabled && timeframes.length > 0
  const wantSingle = enabled && !perTimeframe

  // Hook A: per-timeframe useQueries fanout — UNCONDITIONAL (empty array when inactive)
  const fanoutTfs = perTimeframe ? timeframes : []
  const fanout = useQueries({
    queries: fanoutTfs.map((tf) => {
      const tfFrom = clampFromToFloor(tf.from, floorIso)
      // Campaigns C1 Task 7: a per-arm predicate overrides the base filters for this arm only.
      const tfFilterDims = tf.filter_dims ?? filterDims
      return {
        // tf.filter_dims rides the key — same-window arms with different predicates (e.g. two
        // campaigns compared over identical dates) must not share a cache entry.
        queryKey: [
          'dynamics_panels',
          family,
          kpi,
          factTable,
          filterDims,
          groupBy,
          bucketSize,
          tfFrom,
          tf.to,
          tf.filter_dims ?? null,
        ] as const,
        queryFn: (): Promise<WalkTimelineRow[]> =>
          family === 'ratio'
            ? walkRatio(client, {
                kpi,
                from: tfFrom,
                to: tf.to,
                filter_dims: tfFilterDims,
                group_by: groupBy,
                bucket_size: bucketSize,
              })
            : walkTimeline(client, {
                fact_table: factTable,
                kpi,
                from: tfFrom,
                to: tf.to,
                filter_dims: tfFilterDims,
                group_by: groupBy,
                bucket_size: bucketSize,
                aggregation: 'sum',
              }),
        // FU-28: keep-previous may only bridge a key change WITHIN one walk family — a
        // timeline→ratio flip must render loading, never feed foreign rows to the ratio
        // aggregation (family rides queryKey[1] in this config).
        placeholderData: (
          prev: WalkTimelineRow[] | undefined,
          prevQuery?: { queryKey: readonly unknown[] },
        ) => (prevQuery?.queryKey[1] === family ? prev : undefined),
      }
    }),
  })

  // Hook B: single-window useQuery — UNCONDITIONAL (disabled in per-tf mode)
  // Row 15-light: a non-null baselineFromIso extends the walk's from (ONLY here — the
  // window args stay untouched) and MUST ride in the queryKey or toggling the checkbox
  // would silently serve the unextended cached result.
  // C3 fix: clamp the walk's from to the model floor (reactive) so a pre-floor deep-link
  // refetches once the floor loads; singleFrom in the key ensures the refetch happens.
  const singleFrom = clampFromToFloor(baselineFromIso ?? fromIso, floorIso)
  const single = useQuery({
    queryKey: [
      'dynamics_single',
      family,
      factTable,
      kpi,
      fromIso,
      toIso,
      filterDims,
      groupBy,
      bucketSize,
      baselineFromIso,
      singleFrom,
    ] as const,
    queryFn: async (): Promise<WalkTimelineRow[]> => {
      if (family === 'ratio') {
        return walkRatio(client, {
          kpi,
          from: singleFrom,
          to: toIso,
          filter_dims: filterDims,
          group_by: groupBy,
          bucket_size: bucketSize,
        })
      }
      return walkTimeline(client, {
        fact_table: factTable,
        kpi,
        from: singleFrom,
        to: toIso,
        filter_dims: filterDims,
        group_by: groupBy,
        bucket_size: bucketSize,
        aggregation: 'sum',
      })
    },
    // FU-28: same family-keyed rule as the fanout config above — a family flip must never
    // serve the other family's cached rows into buildSubPeriodPanels.
    placeholderData: (
      prev: WalkTimelineRow[] | undefined,
      prevQuery?: { queryKey: readonly unknown[] },
    ) => (prevQuery?.queryKey[1] === family ? prev : undefined),
    enabled: wantSingle,
  })

  // ---- Derive series groups from whichever source has data ----
  const sourceRows: WalkTimelineRow[] = perTimeframe
    ? fanout.flatMap((r) => (r.data ?? []) as WalkTimelineRow[])
    : ((single.data ?? []) as WalkTimelineRow[])
  // Palette stability under a baseline: extended rows encounter pre-window series first;
  // keep the in-window first-encounter ordering so colors match the flag-off rendering.
  // Boundary at the SAME grain the panel split uses below (the coarser of display unit and wire
  // bucket) — otherwise at fy a series present only in the opening fiscal year's April/May would
  // be ordered as pre-window while its data renders in the first in-window bucket.
  // FU-h: ONE merged-rows showVt decision feeds BOTH the groups pivot and the per-TF
  // builders — key-space agreement is structural, not comment-enforced.
  const mergedShowVt = hasMultipleValueTypes(sourceRows)
  const seriesKeys =
    !perTimeframe && baselineFromIso != null
      ? seriesKeysWindowFirst(
          sourceRows,
          baselineBoundaryIso(fromIso, pointBucketUnit(unit, bucketSize)),
        )
      : pivotTimeline(sourceRows, mergedShowVt).seriesKeys
  const groups: Group[] = assignGroups(seriesKeys, palette, {}, undefined, colorForBase)
  // Task 12: real VT set for the caller's facet-downgrade resolver (Task 3's [] placeholder).
  // ONE site, downstream of sourceRows' dual-mode merge above (fanout flatMap vs single.data) —
  // covers BOTH useDynamicsPanels modes with no duplicate derivation, so neither mode ships
  // undefined (DUAL-MODE gotcha, per FU-02(b) precedent).
  const valueTypes = distinctValueTypes(sourceRows)

  // ---- Build panels ----
  let panels: DynamicsPanel[] = []
  let hiddenCount = 0
  let ratioComponents: RatioComponentsByBucket | null = null

  if (perTimeframe) {
    const rowsPerTf = fanoutTfs.map((_, i) => (fanout[i]?.data ?? []) as WalkTimelineRow[])
    // FU-h: groups above derive from sourceRows (ALL TFs merged) — pass the same showVt
    // decision down so per-TF series keys land in the merged-groups key space.
    const built = buildPerTimeframePanels(rowsPerTf, fanoutTfs, family, unit, groups, mergedShowVt)
    panels = built.panels
    hiddenCount = built.hiddenCount
    // ratioComponents stays null in per-TF mode
  } else if (single.data) {
    const built = buildSubPeriodPanels(
      single.data as WalkTimelineRow[],
      family,
      unit,
      dots,
      groups,
      // The builder needs the WINDOW's from (the split boundary), not the baseline's.
      baselineFromIso != null ? fromIso : null,
      // The grain the ROWS arrive at. The builder reconciles it with `unit` via pointBucketUnit —
      // it is the side that folds the points, so it owns which grain they end up carrying.
      bucketSize,
    )
    panels = built.panels
    hiddenCount = built.hiddenCount
    ratioComponents = built.components
  }

  const isLoading = perTimeframe ? fanout.some((r) => r.isLoading) : single.isLoading
  const isFetching = perTimeframe ? fanout.some((r) => r.isFetching) : single.isFetching
  const isPlaceholderData = perTimeframe
    ? fanout.some((r) => r.isPlaceholderData)
    : single.isPlaceholderData
  const anyError = perTimeframe
    ? (fanout.find((r) => r.isError)?.error ?? null)
    : (single.error ?? null)
  const error = anyError as Error | null
  const isError = perTimeframe ? fanout.some((r) => r.isError) : single.isError

  return {
    panels,
    groups,
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    error,
    hiddenCount,
    ratioComponents,
    valueTypes,
  }
}
